import { useQueryParams } from "@certn/api";
import { Loader } from "@certn/cascadia-react";
import { SetStateAction, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { newOrder, Order, OrderContext } from "./OrderContext";
import {
  loadOrderDraft,
  saveOrderDraft,
  useOrderDraftKey,
} from "./orderDraftStorage";
import { CheckSelectionStep } from "./steps/CheckSelectionStep";
import { NiceJobStep } from "./steps/NiceJobStep";
import { OrderDetailsStep } from "./steps/OrderDetailsStep";
import { PackageDetailsStep } from "./steps/PackageDetailsStep";
import { ReviewStep } from "./steps/ReviewStep";
import { ReviewStepQuickscreen } from "./steps/ReviewStepQuickscreen";
import { useValidateOrderBundles } from "./useValidateOrderBundles";
import { ConfigurationError } from "../../components/ConfigurationError";
import { usePermissions } from "../../components/permissions/PermissionsProvider";

export function CaseOrderPage() {
  const { t } = useTranslation();
  const { params, setParam } = useQueryParams();
  const { hasPermission } = usePermissions();
  const [isLoading, setIsLoading] = useState(false);
  const [checkValidationErrors, setCheckValidationErrors] = useState<
    Set<string>
  >(new Set());
  const draftKey = useOrderDraftKey();
  const [order, _setOrder] = useState<Order>(
    () => loadOrderDraft(draftKey) ?? newOrder
  );
  // blockOrder is true if any check has a validation error
  const blockOrder = checkValidationErrors.size > 0;

  // Legacy setBlockOrder - kept for backwards compatibility but deprecated
  // This now has no effect as we use per-check validation tracking
  const setBlockOrder = useCallback((blocked: boolean) => {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "setBlockOrder is deprecated. Use setCheckValidationError instead."
      );
    }
  }, []);

  const setCheckValidationError = useCallback(
    (checkType: string, hasError: boolean) => {
      setCheckValidationErrors((prev) => {
        if (hasError === prev.has(checkType)) {
          return prev;
        }

        const next = new Set(prev);
        if (hasError) {
          next.add(checkType);
        } else {
          next.delete(checkType);
        }
        return next;
      });
    },
    []
  );

  const clearCheckValidationError = useCallback((checkType: string) => {
    setCheckValidationErrors((prev) => {
      if (!prev.has(checkType)) {
        return prev;
      }

      const next = new Set(prev);
      next.delete(checkType);
      return next;
    });
  }, []);

  const setOrder = useCallback(
    (action: SetStateAction<Order>) => {
      _setOrder((prev) => {
        const newOrder = typeof action === "function" ? action(prev) : action;
        saveOrderDraft(draftKey, newOrder);
        return newOrder;
      });
    },
    [draftKey]
  );

  const packageID = params.package_id;
  const isQuickscreen = params.tab === "quickscreen";

  const steps = useMemo(() => {
    if (isQuickscreen) {
      return [
        <CheckSelectionStep key="CheckSelectionStep" />,
        <ReviewStepQuickscreen key="ReviewStepQuickscreen" />,
      ];
    }

    return [
      packageID ? (
        <PackageDetailsStep key="PackageDetailsStep" />
      ) : (
        <CheckSelectionStep key="CheckSelectionStep" />
      ),
      <OrderDetailsStep key="OrderDetailsStep" />,
      <ReviewStep key="ReviewStep" />,
      <NiceJobStep key="NiceJobStep" />,
    ];
  }, [isQuickscreen, packageID]);

  const rawStep = params["step"] ? Number(params["step"]) - 1 : 0;
  const currentStep = Math.max(0, Math.min(rawStep, steps.length - 1));
  const setCurrentStep = (step: number) => {
    setParam("step", String(step + 1));
  };

  const handleNext = () => {
    setCurrentStep(currentStep + 1);
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const isOrderSubmitted = steps[currentStep]?.key === "NiceJobStep";
  const { isValidatingBundles, isLoadingBundles, isRemovingAllItems } =
    useValidateOrderBundles({
      order,
      setOrder,
      isQuickscreen,
      isEnabled: !isOrderSubmitted,
      onBundlesRemoved: (remainingBundles) => {
        if (
          currentStep > 0 &&
          !remainingBundles.length &&
          !order.checks.length
        ) {
          setCurrentStep(0);
        }
      },
    });

  const userHasPermission =
    hasPermission({
      action: "create",
      target: "case",
      scope: "all",
    }) ||
    hasPermission({
      action: "create",
      target: "case",
      scope: "my_groups",
    });

  if (!userHasPermission) {
    return (
      <ConfigurationError
        message={t(
          "Hmm, it looks like you do not have permission to order cases."
        )}
      />
    );
  }

  return (
    <OrderContext.Provider
      value={{
        blockOrder,
        setBlockOrder,
        setCheckValidationError,
        clearCheckValidationError,
        order,
        isLoading: isLoading || isValidatingBundles,
        setIsLoading,
        setOrder,
        goToNextStep: handleNext,
        goToPrevStep: handlePrev,
        currentStep,
        isQuickscreen,
      }}
    >
      {currentStep > 0 && (isLoadingBundles || isRemovingAllItems) ? (
        <Loader />
      ) : (
        steps[currentStep]
      )}
    </OrderContext.Provider>
  );
}
